import { DuctType, FilterKind, RegType, type FullAirHandlerUnit } from "@eisox/api/core-api";
import { cn } from "@eisox/design-system";

import { getDuctSensors } from "../helpers";
import { Direction, Position } from "../types";
import { AmbientRegulation } from "./ambient-regulation";
import { Exhaust } from "./exhaust";
import { Outside } from "./outside";
import { PlatesHeatExchanger } from "./plates-heat-exchanger";
import * as RecirculationDamper from "./recirculation-damper";
import { RecoveryWheel } from "./recovery-wheel";
import { RecoveryWheelDialog } from "./recovery-wheel-dialog";
import { Return } from "./return";
import { Supply } from "./supply";

interface AirHandlerUnitProps {
  buildingId: string;
  airHandlerUnit: FullAirHandlerUnit;
}

const AirHandlerUnit: React.FC<AirHandlerUnitProps> = ({ buildingId, airHandlerUnit }) => {
  const ductFlow = (type: DuctType) => airHandlerUnit.flows?.find(flow => flow.type === type);
  const filterDefect = (type: DuctType, kind: FilterKind) =>
    airHandlerUnit.filters?.find(filter => filter.type === type && filter.kind === kind)?.pressureSwitchDefect;

  // EXHAUST
  const exhaustDamper = airHandlerUnit.dampers?.find(damper => damper.type === DuctType.EXHAUST);
  const exhaustFlow = ductFlow(DuctType.EXHAUST);
  const exhaustSensors = getDuctSensors({
    co2: airHandlerUnit.exhaustAirCO2,
    co2Defect: airHandlerUnit.exhaustAirCO2Defect,
    humidity: airHandlerUnit.exhaustAirHumidity,
    humidityDefect: airHandlerUnit.exhaustAirHumidityDefect,
    flow: exhaustFlow?.state,
    flowDefect: exhaustFlow?.flowDefect,
    flowSetpoint: exhaustFlow?.setpoint,
    flowActualSetpoint: exhaustFlow?.actualSetpoint,
    pressure: exhaustFlow?.pressure,
    pressureSetpoint: exhaustFlow?.pressureSetpoint,
    pressureActualSetpoint: exhaustFlow?.pressureActualSetpoint,
    pressureDefect: exhaustFlow?.pressureDefect,
  });
  const exhaustAirFilterPressureSwitchDefect = filterDefect(DuctType.EXHAUST, FilterKind.MAIN);

  const exhaustDisplayed =
    airHandlerUnit.exhaustAirTemperature !== undefined ||
    !!exhaustDamper ||
    !!exhaustSensors ||
    exhaustAirFilterPressureSwitchDefect !== undefined;

  // RETURN
  const returnDamper = airHandlerUnit.dampers?.find(damper => damper.type === DuctType.RETURN);
  const returnFlow = ductFlow(DuctType.RETURN);
  const returnSensors = getDuctSensors({
    co2: airHandlerUnit.returnAirCO2,
    co2Defect: airHandlerUnit.returnAirCO2Defect,
    co2Setpoint: airHandlerUnit.returnAirCO2Setpoint,
    co2ActualSetpoint: airHandlerUnit.returnAirCO2ActualSetpoint,
    humidity: airHandlerUnit.returnAirHumidity,
    humidityDefect: airHandlerUnit.returnAirHumidityDefect,
    humiditySetpoint: airHandlerUnit.returnAirHumiditySetpoint,
    humidityActualSetpoint: airHandlerUnit.returnAirHumidityActualSetpoint,
    flow: returnFlow?.state,
    flowDefect: returnFlow?.flowDefect,
    flowSetpoint: returnFlow?.setpoint,
    flowActualSetpoint: returnFlow?.actualSetpoint,
    pressure: returnFlow?.pressure,
    pressureSetpoint: returnFlow?.pressureSetpoint,
    pressureActualSetpoint: returnFlow?.pressureActualSetpoint,
    pressureDefect: returnFlow?.pressureDefect,
  });
  const returnFan = airHandlerUnit.fans?.find(fan => fan.type === DuctType.RETURN);
  const returnAirFilterPressureSwitchDefect = filterDefect(DuctType.RETURN, FilterKind.MAIN);

  const returnDisplayed =
    airHandlerUnit.returnAirTemperature !== undefined ||
    !!returnDamper ||
    !!returnSensors ||
    !!returnFan ||
    returnAirFilterPressureSwitchDefect !== undefined;

  // OUTSIDE
  const outsideDamper = airHandlerUnit.dampers?.find(damper => damper.type === DuctType.OUTSIDE);
  const outsideFlow = ductFlow(DuctType.OUTSIDE);
  const outsideSensors = getDuctSensors({
    co2: airHandlerUnit.outsideAirCO2,
    co2Defect: airHandlerUnit.outsideAirCO2Defect,
    humidity: airHandlerUnit.outsideAirHumidity,
    humidityDefect: airHandlerUnit.outsideAirHumidityDefect,
    flow: outsideFlow?.state,
    flowDefect: outsideFlow?.flowDefect,
    flowSetpoint: outsideFlow?.setpoint,
    flowActualSetpoint: outsideFlow?.actualSetpoint,
    pressure: outsideFlow?.pressure,
    pressureSetpoint: outsideFlow?.pressureSetpoint,
    pressureActualSetpoint: outsideFlow?.pressureActualSetpoint,
    pressureDefect: outsideFlow?.pressureDefect,
  });
  const outsideAirFilterPressureSwitchDefect = filterDefect(DuctType.OUTSIDE, FilterKind.MAIN);
  const outsideAirPreFilterPressureSwitchDefect = filterDefect(DuctType.OUTSIDE, FilterKind.PRE);
  const outsideCoils = airHandlerUnit.coils?.filter(coil => coil.type === DuctType.OUTSIDE);

  const outsideDisplayed =
    airHandlerUnit.outsideAirTemperature !== undefined ||
    !!outsideSensors ||
    !!outsideDamper ||
    !!outsideCoils?.length ||
    outsideAirFilterPressureSwitchDefect !== undefined ||
    outsideAirPreFilterPressureSwitchDefect !== undefined;

  // SUPPLY
  const supplyDamper = airHandlerUnit.dampers?.find(damper => damper.type === DuctType.SUPPLY);
  const supplyFlow = ductFlow(DuctType.SUPPLY);
  const supplySensors = getDuctSensors({
    co2: airHandlerUnit.supplyAirCO2,
    co2Defect: airHandlerUnit.supplyAirCO2Defect,
    co2Setpoint: airHandlerUnit.supplyAirCO2Setpoint,
    co2ActualSetpoint: airHandlerUnit.supplyAirCO2ActualSetpoint,
    humidity: airHandlerUnit.supplyAirHumidity,
    humidityDefect: airHandlerUnit.supplyAirHumidityDefect,
    humiditySetpoint: airHandlerUnit.supplyAirHumiditySetpoint,
    humidityActualSetpoint: airHandlerUnit.supplyAirHumidityActualSetpoint,
    flow: supplyFlow?.state,
    flowDefect: supplyFlow?.flowDefect,
    flowSetpoint: supplyFlow?.setpoint,
    flowActualSetpoint: supplyFlow?.actualSetpoint,
    pressure: supplyFlow?.pressure,
    pressureSetpoint: supplyFlow?.pressureSetpoint,
    pressureActualSetpoint: supplyFlow?.pressureActualSetpoint,
    pressureDefect: supplyFlow?.pressureDefect,
  });
  const supplyFan = airHandlerUnit.fans?.find(fan => fan.type === DuctType.SUPPLY);
  const supplyAirFilterPressureSwitchDefect = filterDefect(DuctType.SUPPLY, FilterKind.MAIN);
  const terminalAirFilterPressureSwitchDefect = filterDefect(DuctType.SUPPLY, FilterKind.TERMINAL);
  const supplyCoils = airHandlerUnit.coils
    ?.filter(coil => coil.type === DuctType.SUPPLY)
    .sort((a, b) => a.index - b.index);

  // A damper without a duct `type` is a recirculation damper: it hangs off the AHU, not off a duct.
  const recirculationDamper = airHandlerUnit.dampers?.find(damper => damper.type === undefined);
  // With heat recovery, recirculation happens downstream of it: between return and supply air.
  const recirculationDamperSide =
    airHandlerUnit.recoveryWheel || airHandlerUnit.platesHeatExchanger ? Direction.RIGHT : Direction.LEFT;
  const leftRecirculationDamper = recirculationDamperSide === Direction.LEFT ? recirculationDamper : undefined;
  const rightRecirculationDamper = recirculationDamperSide === Direction.RIGHT ? recirculationDamper : undefined;

  const supplyDisplayed =
    airHandlerUnit.supplyAirTemperature !== undefined ||
    !!supplyDamper ||
    !!supplySensors ||
    !!supplyFan ||
    !!airHandlerUnit.humidifier ||
    !!airHandlerUnit.dehumidifier ||
    supplyAirFilterPressureSwitchDefect !== undefined ||
    terminalAirFilterPressureSwitchDefect !== undefined;

  // The automate can compute a temperature setpoint on the supply duct, on the return duct and on
  // the room at once, but only one of them is the objective: `regType` names it. The other two are
  // means, and showing them beside the objective would put three targets on the same scheme.
  const regulatedBySupply = airHandlerUnit.regType === RegType.SUPPLY_SETPOINT;
  const regulatedByReturn = airHandlerUnit.regType === RegType.RETURN_SETPOINT;
  const regulatedByAmbient = airHandlerUnit.regType === RegType.AMBIENT_SETPOINT;
  const ambientRegulationDisplayed =
    regulatedByAmbient &&
    (airHandlerUnit.ambientTemperature !== undefined ||
      airHandlerUnit.ambientTemperatureActualSetpoint !== undefined ||
      airHandlerUnit.ambientTemperatureSetpoint !== undefined ||
      airHandlerUnit.ambientTemperatureCoolingActualSetpoint !== undefined ||
      airHandlerUnit.ambientTemperatureCoolingSetpoint !== undefined);

  const topRowDisplayed = exhaustDisplayed || returnDisplayed;
  const bottomRowDisplayed = outsideDisplayed || supplyDisplayed;
  const isSingleRow = !topRowDisplayed || !bottomRowDisplayed;

  return (
    <RecirculationDamper.Root side={recirculationDamperSide}>
      <div className="gap-5 flex w-max flex-col items-end">
        <div
          className={cn(
            "gap-y-10.5 grid w-max grid-cols-[auto_auto_auto] grid-rows-[1fr_1fr] [grid-template-areas:'exhaust_recoveryWheel_return'_'outside_recoveryWheel_supply']",
            airHandlerUnit.platesHeatExchanger &&
              "[grid-template-areas:'outside_platesHeatExchanger_return'_'exhaust_platesHeatExchanger_supply']",
            isSingleRow && "gap-y-0 grid-rows-[auto]",
          )}
        >
          {(exhaustDisplayed || returnDisplayed) && (
            <Exhaust
              buildingId={buildingId}
              airHandlerUnitId={airHandlerUnit.id}
              position={airHandlerUnit.platesHeatExchanger ? Position.BOTTOM : Position.TOP}
              temperature={airHandlerUnit.exhaustAirTemperature}
              temperatureDefect={airHandlerUnit.exhaustAirTemperatureDefect}
              damper={exhaustDamper}
              sensors={exhaustSensors}
              airFilterPressureSwitchDefect={exhaustAirFilterPressureSwitchDefect}
              recirculationDamper={leftRecirculationDamper}
            />
          )}
          {(returnDisplayed || exhaustDisplayed) && (
            <Return
              buildingId={buildingId}
              airHandlerUnitId={airHandlerUnit.id}
              temperature={airHandlerUnit.returnAirTemperature}
              temperatureDefect={airHandlerUnit.returnAirTemperatureDefect}
              setpoint={regulatedByReturn ? airHandlerUnit.returnAirSetpoint : undefined}
              actualSetpoint={regulatedByReturn ? airHandlerUnit.returnAirActualSetpoint : undefined}
              coolingSetpoint={regulatedByReturn ? airHandlerUnit.returnAirCoolingSetpoint : undefined}
              coolingActualSetpoint={regulatedByReturn ? airHandlerUnit.returnAirCoolingActualSetpoint : undefined}
              damper={returnDamper}
              sensors={returnSensors}
              airFilterPressureSwitchDefect={returnAirFilterPressureSwitchDefect}
              fan={returnFan}
              recirculationDamper={rightRecirculationDamper}
            />
          )}
          {airHandlerUnit.recoveryWheel && (
            <RecoveryWheelDialog
              buildingId={buildingId}
              airHandlerUnitId={airHandlerUnit.id}
              recoveryWheel={airHandlerUnit.recoveryWheel}
            >
              <RecoveryWheel
                speed={airHandlerUnit.recoveryWheel.state}
                efficiency={airHandlerUnit.recoveryWheel.efficiency}
                defect={airHandlerUnit.recoveryWheel.defect}
                missmatchDefect={airHandlerUnit.recoveryWheel.missmatchDefect}
              />
            </RecoveryWheelDialog>
          )}
          {airHandlerUnit.platesHeatExchanger && (
            <PlatesHeatExchanger efficiency={airHandlerUnit.platesHeatExchangerEfficiency} />
          )}
          {(outsideDisplayed || supplyDisplayed) && (
            <Outside
              buildingId={buildingId}
              airHandlerUnitId={airHandlerUnit.id}
              position={
                (!exhaustDisplayed && !returnDisplayed) || airHandlerUnit.platesHeatExchanger
                  ? Position.TOP
                  : Position.BOTTOM
              }
              temperature={airHandlerUnit.outsideAirTemperature}
              temperatureDefect={airHandlerUnit.outsideAirTemperatureDefect}
              coils={outsideCoils}
              damper={outsideDamper}
              sensors={outsideSensors}
              airFilterPressureSwitchDefect={outsideAirFilterPressureSwitchDefect}
              preFilterPressureSwitchDefect={outsideAirPreFilterPressureSwitchDefect}
              antiFreezeDefect={airHandlerUnit.antiFreezeDefect}
              recirculationDamper={leftRecirculationDamper !== undefined}
            />
          )}
          {(supplyDisplayed || outsideDisplayed) && (
            <Supply
              buildingId={buildingId}
              airHandlerUnitId={airHandlerUnit.id}
              position={!exhaustDisplayed && !returnDisplayed ? Position.TOP : Position.BOTTOM}
              temperature={airHandlerUnit.supplyAirTemperature}
              temperatureDefect={airHandlerUnit.supplyAirTemperatureDefect}
              coils={supplyCoils}
              damper={supplyDamper}
              fan={supplyFan}
              humidifier={airHandlerUnit.humidifier}
              dehumidifier={airHandlerUnit.dehumidifier}
              airFilterPressureSwitchDefect={supplyAirFilterPressureSwitchDefect}
              terminalFilterPressureSwitchDefect={terminalAirFilterPressureSwitchDefect}
              setpoint={regulatedBySupply ? airHandlerUnit.supplyAirSetpoint : undefined}
              actualSetpoint={regulatedBySupply ? airHandlerUnit.supplyAirActualSetpoint : undefined}
              coolingSetpoint={regulatedBySupply ? airHandlerUnit.supplyAirCoolingSetpoint : undefined}
              coolingActualSetpoint={regulatedBySupply ? airHandlerUnit.supplyAirCoolingActualSetpoint : undefined}
              sensors={supplySensors}
              pressureSwitchDefect={airHandlerUnit.airDefect}
              recirculationDamper={rightRecirculationDamper !== undefined}
            />
          )}
        </div>
        {ambientRegulationDisplayed && (
          <AmbientRegulation
            buildingId={buildingId}
            airHandlerUnitId={airHandlerUnit.id}
            temperature={airHandlerUnit.ambientTemperature}
            setpoint={airHandlerUnit.ambientTemperatureSetpoint}
            actualSetpoint={airHandlerUnit.ambientTemperatureActualSetpoint}
            coolingSetpoint={airHandlerUnit.ambientTemperatureCoolingSetpoint}
            coolingActualSetpoint={airHandlerUnit.ambientTemperatureCoolingActualSetpoint}
          />
        )}
      </div>
    </RecirculationDamper.Root>
  );
};

AirHandlerUnit.displayName = "AirHandlerUnit";

export { AirHandlerUnit };
export type { AirHandlerUnitProps };
